import PropTypes from 'prop-types';
import React, {
  KeyboardEvent,
  useEffect,
  useState,
  FunctionComponent,
} from 'react';
import { isNil } from 'lodash';
import { formatToPrecision, parseFloatValue } from './util';
import Input from './Input';
import './Input.scss';
import { useComponentVisible } from '../../../hooks/useComponentVisible';

const DEFAULT_DECIMAL_PRECISION = 6;

const getPercentFromValue = (apr: string) =>
  formatToPrecision(parseFloatValue(apr) * 100, DEFAULT_DECIMAL_PRECISION);
const getValueFromPercent = (percent: string) =>
  parseFloatValue(percent) * 0.01;

interface PercentageInputProps {
  value?: string | number | undefined;
  onChange?: (value: number | null) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  title?: string;
  placeholder?: string;
  step?: string;
  disabled?: boolean;
  required?: boolean;
  displayPercentSign?: boolean;
}

const PercentageInput: FunctionComponent<PercentageInputProps> = props => {
  const {
    ref,
    isComponentVisible,
    setIsComponentVisible,
  } = useComponentVisible(true);
  const { value } = props;
  const getValue =
    isNil(value) || (!props.placeholder && value === 0)
      ? ''
      : getPercentFromValue(value.toString());

  const [displayValue, setDisplayValue] = useState(getValue);
  const [hasFocus, setHasFocus] = useState(false);

  const changeHandler = (percent: string | boolean) => {
    if (!props.onChange || typeof percent === 'boolean') {
      return;
    }
    setDisplayValue(percent);
    if (percent.trim() === '' || isNaN(Number(percent))) {
      props.onChange(null);
    } else {
      props.onChange(getValueFromPercent(percent));
    }
  };

  useEffect(() => {
    if (getValue !== displayValue && !hasFocus) {
      setDisplayValue(getValue);
    }
  }, [value]);

  useEffect(() => {
    // if clicked outside, set the current value to display value.
    if (!isComponentVisible) {
      if (getValue !== displayValue) {
        setDisplayValue(getValue);
      }
    }
  }, [isComponentVisible]);

  return (
    <div className="finance-input percentage-input-container" ref={ref as any}>
      <Input
        type="number"
        className={`percentage-input ${props.className ? props.className : ''}`}
        disabled={props.disabled}
        required={props.required}
        onChange={changeHandler}
        value={displayValue}
        title={props.title}
        placeholder={props.placeholder ?? '0'}
        onFocus={() => {
          setHasFocus(true);
          setIsComponentVisible(true);
        }}
        onBlur={() => setHasFocus(false)}
        step={props.step}
        onKeyDown={props.onKeyDown}
      />
      {props.displayPercentSign && '%'}
    </div>
  );
};

PercentageInput.defaultProps = {
  displayPercentSign: true,
};

PercentageInput.propTypes = {
  value: PropTypes.number,
  title: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  displayPercentSign: PropTypes.bool,
  step: PropTypes.string,
  onKeyDown: PropTypes.func,
};

export default PercentageInput;
